<template>
  <div class="exam-taking-container">
    <div class="exam-taking-card" v-loading="loading">
      <template v-if="examData && !loading">
        <!-- Header: Timer + Progress -->
        <div class="exam-header">
          <div class="exam-progress">
            Question {{ currentIndex + 1 }} / {{ examData.total_questions }}
          </div>
          <div v-if="examData.time_limit_seconds > 0" class="exam-timer" :class="{ 'timer-warning': timeRemaining <= 60 }">
            <i class="el-icon-time"></i> {{ formatTime(timeRemaining) }}
          </div>
        </div>

        <!-- Question -->
        <div class="question-area">
          <div class="question-text">
            <span class="question-number">Q{{ currentIndex + 1 }}.</span>
            {{ currentQuestion.question }}
          </div>
          <div class="options-list">
            <div
              v-for="(option, idx) in currentQuestion.options"
              :key="idx"
              class="option-item"
              :class="{ selected: answers[currentIndex] === labels[idx] }"
              @click="selectOption(labels[idx])"
            >
              <span class="option-label">{{ labels[idx] }}</span>
              <span class="option-text">{{ option }}</span>
            </div>
          </div>
        </div>

        <!-- Navigation -->
        <div class="exam-nav">
          <el-button
            :disabled="currentIndex === 0"
            @click="currentIndex--"
            size="medium"
          >Previous</el-button>
          <div class="question-dots">
            <span
              v-for="(q, idx) in examData.questions"
              :key="idx"
              class="dot"
              :class="{ active: idx === currentIndex, answered: answers[idx] != null }"
              @click="currentIndex = idx"
            >{{ idx + 1 }}</span>
          </div>
          <el-button
            v-if="currentIndex < examData.total_questions - 1"
            type="primary"
            @click="currentIndex++"
            size="medium"
          >Next</el-button>
          <el-button
            v-else
            type="success"
            @click="confirmSubmit"
            size="medium"
          >Submit</el-button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { postRequest } from '@/apis/front'
import { mapState } from 'vuex'

export default {
  name: 'ExamTaking',
  data() {
    return {
      loading: true,
      examData: null,
      currentIndex: 0,
      answers: {},
      labels: ['A', 'B', 'C', 'D'],
      timeRemaining: 0,
      timer: null
    }
  },
  computed: {
    ...mapState(['user_id']),
    attemptId() {
      return this.$route.query.attempt_id
    },
    collectionId() {
      return this.$route.query.collection_id
    },
    currentQuestion() {
      return this.examData?.questions?.[this.currentIndex] || {}
    }
  },
  mounted() {
    this.loadExam()
    window.addEventListener('beforeunload', this.handleBeforeUnload)
  },
  beforeDestroy() {
    if (this.timer) clearInterval(this.timer)
    window.removeEventListener('beforeunload', this.handleBeforeUnload)
  },
  methods: {
    async loadExam() {
      this.loading = true
      try {
        // Try to resume in-progress exam first
        const response = await postRequest('getExamProgress', {
          attempt_id: this.attemptId,
          user_id: this.user_id
        })
        if (response.data.code === 0) {
          this.examData = response.data.data
          // Restore saved answers
          response.data.data.questions.forEach((q, idx) => {
            if (q.student_answer) {
              this.$set(this.answers, idx, q.student_answer)
            }
          })
          // Set timer
          if (this.examData.time_remaining_seconds > 0) {
            this.timeRemaining = this.examData.time_remaining_seconds
            this.startTimer()
          } else if (this.examData.time_limit_seconds > 0) {
            this.timeRemaining = this.examData.time_limit_seconds
            this.startTimer()
          }
        } else {
          this.$message.error(response.data.msg || 'Failed to load exam')
          this.$router.replace('/my-learning')
        }
      } catch (error) {
        console.error('Error loading exam:', error)
        this.$message.error('Failed to load exam')
        this.$router.replace('/my-learning')
      } finally {
        this.loading = false
      }
    },
    selectOption(label) {
      this.$set(this.answers, this.currentIndex, label)
      // Auto-save answer
      postRequest('cacheExamAnswer', {
        attempt_id: this.attemptId,
        user_id: this.user_id,
        question_index: this.currentIndex,
        answer: label
      }).catch(() => {})
    },
    startTimer() {
      this.timer = setInterval(() => {
        this.timeRemaining--
        if (this.timeRemaining <= 0) {
          clearInterval(this.timer)
          this.autoSubmit()
        }
      }, 1000)
    },
    formatTime(seconds) {
      const m = Math.floor(seconds / 60)
      const s = seconds % 60
      return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
    },
    confirmSubmit() {
      const unanswered = this.examData.total_questions - Object.keys(this.answers).length
      let msg = 'Are you sure you want to submit the exam?'
      if (unanswered > 0) {
        msg = `You have ${unanswered} unanswered question${unanswered > 1 ? 's' : ''}. Are you sure you want to submit?`
      }
      this.$confirm(msg, 'Submit Exam', {
        confirmButtonText: 'Submit',
        cancelButtonText: 'Cancel',
        type: 'warning'
      }).then(() => {
        this.doSubmit()
      }).catch(() => {})
    },
    async autoSubmit() {
      this.$message.warning('Time is up! Submitting your exam...')
      await this.doSubmit()
    },
    async doSubmit() {
      try {
        const response = await postRequest('submitExam', {
          attempt_id: this.attemptId,
          user_id: this.user_id
        })
        if (response.data.code === 0) {
          this.$router.replace({
            path: '/exam-result',
            query: { attempt_id: this.attemptId }
          })
        } else {
          this.$message.error(response.data.msg || 'Failed to submit exam')
        }
      } catch (error) {
        console.error('Error submitting exam:', error)
        this.$message.error('Failed to submit exam')
      }
    },
    handleBeforeUnload(e) {
      e.preventDefault()
      e.returnValue = ''
    }
  }
}
</script>

<style scoped>
.exam-taking-container {
  min-height: 100vh;
  background: #f7f8fc;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 40px 24px;
}

.exam-taking-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  max-width: 800px;
  width: 100%;
  padding: 36px 40px;
  min-height: 400px;
}

/* Header */
.exam-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  padding-bottom: 16px;
  border-bottom: 1px solid #ebeef5;
}

.exam-progress {
  font-size: 14px;
  font-weight: 600;
  color: #606266;
}

.exam-timer {
  font-size: 18px;
  font-weight: 700;
  color: #409eff;
  display: flex;
  align-items: center;
  gap: 6px;
}

.timer-warning {
  color: #F56C6C;
  animation: pulse 1s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

/* Question */
.question-area {
  margin-bottom: 32px;
}

.question-text {
  font-size: 17px;
  font-weight: 600;
  color: #1a1a2e;
  line-height: 1.6;
  margin-bottom: 24px;
}

.question-number {
  color: #409eff;
  margin-right: 6px;
}

.options-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.option-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 18px;
  border: 2px solid #e4e7ed;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
  background: #fafbfc;
}

.option-item:hover {
  border-color: #409eff;
  background: #ecf5ff;
}

.option-item.selected {
  border-color: #409eff;
  background: #ecf5ff;
}

.option-label {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #e4e7ed;
  color: #606266;
  font-weight: 700;
  font-size: 13px;
  flex-shrink: 0;
}

.option-item.selected .option-label {
  background: #409eff;
  color: #fff;
}

.option-text {
  font-size: 15px;
  color: #303133;
  line-height: 1.6;
  padding-top: 3px;
}

/* Navigation */
.exam-nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding-top: 24px;
  border-top: 1px solid #ebeef5;
}

.question-dots {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: center;
  flex: 1;
}

.dot {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  border: 2px solid #dcdfe6;
  color: #909399;
  background: #fff;
  transition: all 0.15s;
}

.dot.answered {
  background: #e1f3d8;
  border-color: #67C23A;
  color: #67C23A;
}

.dot.active {
  background: #409eff;
  border-color: #409eff;
  color: #fff;
}

/* Responsive */
@media (max-width: 768px) {
  .exam-taking-container {
    padding: 16px;
  }
  .exam-taking-card {
    padding: 20px 16px;
  }
  .exam-header {
    flex-direction: column;
    gap: 8px;
    align-items: flex-start;
  }
  .question-text {
    font-size: 15px;
  }
  .option-item {
    padding: 12px 14px;
  }
  .exam-nav {
    flex-wrap: wrap;
  }
  .question-dots {
    order: 3;
    width: 100%;
    margin-top: 12px;
  }
}
</style>
